import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════
// Zod schemas shared between backend (DTO validation) and frontend
// (form validation). Single source of truth.
// ═══════════════════════════════════════════════════════════════════

// ───── Auth ─────
export const loginSchema = z.object({
  email: z.string().min(3, 'Enter email or EMP code'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const signupSchema = z.object({
  companyName: z.string().min(2),
  brandName: z.string().optional(),
  email: z.string().email(),
  mobile: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit Indian mobile'),
  password: z.string().min(8),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});
export type SignupInput = z.infer<typeof signupSchema>;

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

// ───── Pincode ─────
export const pincodeSchema = z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits');

// ───── Indian mobile ─────
export const indianMobileSchema = z
  .string()
  .regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit Indian mobile (starts with 6-9)');

// ───── GST ─────
export const gstNumberSchema = z
  .string()
  .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GSTIN');

// ───── PAN ─────
export const panNumberSchema = z
  .string()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN');

// ───── Order create (B2C) ─────
export const orderProductSchema = z.object({
  sku: z.string().optional(),
  name: z.string().min(1),
  qty: z.number().int().positive(),
  weight: z.number().positive(),
  value: z.number().nonnegative(),
  hsn: z.string().optional(),
});

export const createOrderB2cSchema = z.object({
  merchantId: z.number().int().positive(),
  courierId: z.number().int().positive().optional(),
  orderNo: z.string().min(1).max(64),
  deliveryType: z.enum(['COD', 'PREPAID']),
  codValue: z.number().nonnegative().default(0),
  invoiceValue: z.number().positive(),

  products: z.array(orderProductSchema).min(1),

  dimensions: z
    .object({
      length: z.number().positive(),
      breadth: z.number().positive(),
      height: z.number().positive(),
    })
    .optional(),

  customerName: z.string().min(2).max(255),
  customerMobile: indianMobileSchema,
  customerAltMobile: indianMobileSchema.optional(),
  customerEmail: z.string().email().optional(),
  customerAddress: z.string().min(10),
  customerCity: z.string().min(2),
  customerState: z.string().min(2),
  customerPincode: pincodeSchema,
  customerLandmark: z.string().optional(),

  pickupPincode: pincodeSchema,
  pickupAddress: z.string().min(10),
  warehouseId: z.number().int().positive().optional(),
});
export type CreateOrderB2cInput = z.infer<typeof createOrderB2cSchema>;

// ───── Order create (B2B) ─────
export const createOrderB2bSchema = createOrderB2cSchema.extend({
  totalBoxes: z.number().int().positive(),
  insuranceType: z.enum(['CARRIER_RISK', 'OWNER_RISK']).optional(),
  freightCollection: z.enum(['BILL_TO_CLIENT', 'COLLECT_ON_DELIVERY']),
  pickupType: z.enum(['FM_PICKUP', 'SELF_DROP']).default('FM_PICKUP'),
  subOrders: z.array(z.any()).optional(),
});
export type CreateOrderB2bInput = z.infer<typeof createOrderB2bSchema>;

// ───── Merchant ─────
export const createMerchantSchema = z.object({
  companyName: z.string().min(2).max(255),
  brandName: z.string().optional(),
  email: z.string().email(),
  mobile: indianMobileSchema,
  serviceType: z.enum(['B2C', 'B2B', 'BOTH']).default('BOTH'),
});

export const merchantKycSchema = z.object({
  panNumber: panNumberSchema.optional(),
  gstNumber: gstNumberSchema.optional(),
  aadharNumber: z.string().regex(/^\d{12}$/).optional(),
});

export const merchantBankingSchema = z.object({
  accountHolderName: z.string().min(2),
  accountNumber: z.string().min(9).max(18),
  bankName: z.string().min(2),
  branchName: z.string().optional(),
  ifscCode: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code'),
});

// ───── Courier ─────
export const createCourierSchema = z.object({
  name: z.string().min(2).max(100),
  code: z.string().min(2).max(20).regex(/^[a-z0-9_-]+$/, 'Lowercase, alphanumeric only'),
  brand: z.string().optional(),
  serviceType: z.enum(['AIR', 'PRIME', 'SURFACE', 'EXPRESS', 'REVERSE']),
  orderType: z.enum(['B2C', 'B2B']),
  isAggregator: z.boolean().default(false),
  volumetricDivisor: z.number().int().positive().default(5000),
  tatDays: z.number().int().positive().optional(),
  maxLiability: z.number().nonnegative().optional(),
  contactName: z.string().optional(),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().optional(),
  reportEmails: z.array(z.string().email()).optional(),
});

// ───── NDR Action ─────
export const ndrActionSchema = z.object({
  ndrId: z.number().int().positive(),
  action: z.enum(['REATTEMPT', 'CHANGE_ADDRESS', 'CHANGE_PHONE', 'RTO']),
  remarks: z.string().optional(),
  newAddress: z.string().optional(),
  newPhone: indianMobileSchema.optional(),
  reattemptDate: z.string().datetime().optional(),
});

// ───── Wallet recharge ─────
export const walletRechargeSchema = z.object({
  amount: z.number().positive().min(100, 'Minimum recharge ₹100'),
  paymentGateway: z.enum(['PAYU', 'RAZORPAY', 'MANUAL']).default('PAYU'),
});

// ───── Pagination ─────
export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(200).default(20),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationInput = z.infer<typeof paginationSchema>;
